import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import moment from "moment";
import { debounce } from "lodash";
import {
  Building2,
  CheckCircle2,
  Clock,
  XCircle,
  Home,
  ShieldAlert,
  IndianRupee,
  PiggyBank,
  Users,
  Handshake,
} from "lucide-react";
import InnerLayout from "../../components/layouts/inner-layout";
import DynamicTransactionTable from "../../components/tables/dynamic-transaction-table";
import LoadingSpinner from "../../components/helper-components/loading-spinner";
import TransSummaryComp from "../../components/helper-components/trans-summary-comp";
import BadgesComponent from "../../components/tables/badges-component";
import errorToast from "../../components/helper-components/error-toast";
import { DataContext } from "../../components/layouts/main-layout";
import CocoOverviewFilter from "../../components/store-information/coco-store-overview/coco-overview-filter";
import StoreStatusPieChart from "../../components/store-information/coco-store-overview/store-status-pie-chart";
import MonthlyCreationBarChart from "../../components/store-information/coco-store-overview/monthly-creation-bar-chart";
import CollectionTrendChart from "../../components/store-information/coco-store-overview/collection-trend-chart";
import {
  fetchCocoStoreAnalytics,
  fetchCocoStoreDashboardCounts,
  fetchCocoStoreFilterData,
  fetchCocoStoreList,
} from "../../redux/apis/coco-store-overview-api";

const MODULE_NAME = "store_document_process";

const STAT_TILES = [
  { key: "totalStores", label: "Total Stores", icon: Building2, tone: "blue" },
  { key: "approvedStores", label: "Approved Stores", icon: CheckCircle2, tone: "green" },
  { key: "pendingStores", label: "Pending Stores", icon: Clock, tone: "orange" },
  { key: "rejectedStores", label: "Rejected Stores", icon: XCircle, tone: "red" },
  { key: "pendingRentPayments", label: "Pending Rent Payments", icon: Home, tone: "orange" },
  { key: "pendingSdPayments", label: "Pending SD Payments", icon: ShieldAlert, tone: "orange" },
  { key: "rentCollectedThisMonth", label: "Rent Collected This Month", icon: IndianRupee, tone: "green", isCurrency: true },
  { key: "sdCollected", label: "SD Collected", icon: PiggyBank, tone: "green", isCurrency: true },
  { key: "totalLandlords", label: "Total Landlords", icon: Users, tone: "violet" },
  { key: "totalBrokers", label: "Total Brokers", icon: Handshake, tone: "blue" },
];

const TONE_CLASSES = {
  blue: { bgClass: "bg-[var(--trans-stats-con-blue-bg)]", fg: "var(--trans-stats-con-blue-fg)" },
  green: { bgClass: "bg-[var(--trans-stats-con-green-bg)]", fg: "var(--trans-stats-con-green-fg)" },
  orange: { bgClass: "bg-[var(--trans-stats-con-orange-bg)]", fg: "var(--trans-stats-con-orange-fg)" },
  red: { bgClass: "bg-[var(--trans-stats-con-red-bg)]", fg: "var(--trans-stats-con-red-fg)" },
  violet: { bgClass: "bg-[var(--trans-stats-con-violet2-bg)]", fg: "var(--trans-stats-con-voilet2-fg)" },
};

const tabHeading = [
  { label: "STORE CODE", value: "store_code", display: true, column: true, minWidth: 110 },
  { label: "STORE NAME", value: "store_name", display: true, column: true, minWidth: 180 },
  { label: "CITY", value: "city", display: true, column: false, minWidth: 110 },
  { label: "STATE", value: "state", display: true, column: false, minWidth: 120 },
  { label: "BROKER", value: "broker_name", display: false, column: false, minWidth: 120 },
  { label: "CREATOR", value: "creator_name", display: false, column: false, minWidth: 120 },
  { label: "APPROVER", value: "approver_name", display: false, column: false, minWidth: 120 },
  { label: "STORE STATUS", value: "store_status", display: true, column: false, minWidth: 120 },
  { label: "RENT STATUS", value: "rent_status", display: false, column: false, minWidth: 110 },
  { label: "SD STATUS", value: "sd_status", display: false, column: false, minWidth: 110 },
  { label: "RENT DUE", value: "rent_due", display: false, column: false, minWidth: 110 },
  { label: "SD DUE", value: "sd_due", display: false, column: false, minWidth: 110 },
  { label: "CREATED", value: "created_date", display: true, column: false, minWidth: 110 },
];

const initialFilter = {
  sort_by: "",
  sort_order: "",
  selectColumn: "store_code",
  from: "",
  to: "",
  inputValue: "",
  creator_id: "",
  city: "",
  state: "",
  rent_status: "",
  sd_status: "",
};

// Normalizes option lists from /filter-data — entries may be plain strings
// or objects, and object key names aren't confirmed against the backend yet.
// Entries that resolve to a blank label/value are dropped: react-select
// otherwise renders a "selected but empty" chip whenever filterData's field
// (initially "") happens to match a blank option's value.
function toSelectOptions(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (item && typeof item === "object") {
        const label =
          item.name ??
          item.label ??
          item.user_name ??
          item.creator_name ??
          item.broker_name ??
          item.city ??
          item.state ??
          item.id ??
          item.value ??
          "";
        const value =
          item.user_id ?? item.id ?? item.value ?? item.creator_id ?? item.broker_id ?? label;
        return { label: String(label).trim(), value };
      }
      return { label: String(item ?? "").trim(), value: item };
    })
    .filter((opt) => opt.label && opt.value !== undefined && opt.value !== null && opt.value !== "");
}

// Pulls the first array found under any of several candidate key names —
// the exact key names /analytics returns aren't confirmed against the
// backend yet, so common camelCase/snake_case variants are all tried.
function pickSeries(source, candidates) {
  for (const key of candidates) {
    const v = source?.[key];
    if (Array.isArray(v)) return v;
  }
  return [];
}

// Normalizes analytics series points — field names aren't confirmed against
// the backend yet, so several common shapes are accepted defensively.
function normalizeSeries(list, valueKeys = ["count", "total", "amount", "value"]) {
  if (!Array.isArray(list)) return [];
  return list.map((p) => {
    const name = p?.month ?? p?.name ?? p?.label ?? p?.range ?? p?.period ?? p?.date ?? "";
    let value = 0;
    for (const k of valueKeys) {
      if (p?.[k] !== undefined && p?.[k] !== null) {
        value = Number(p[k]);
        break;
      }
    }
    return { name: String(name), value };
  });
}

const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-xl p-4 border border-[var(--dashboard-gray-200)]">
    <h3 className="text-sm font-semibold text-[var(--dashboard-gray-900)] mb-3">
      {title}
    </h3>
    {children}
  </div>
);

const CocoStoreOverview = () => {
  const { permissionFn } = useContext(DataContext);

  const [dashboardCounts, setDashboardCounts] = useState({});
  const [filterOptions, setFilterOptions] = useState({ creators: [], cities: [], states: [] });
  const [analytics, setAnalytics] = useState({
    monthlyStoreCreation: [],
    rentCollectionTrend: [],
    sdCollectionTrend: [],
  });

  const [storeList, setStoreList] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState(initialFilter);

  // One-time load: dashboard counts, filter option lists, and analytics
  // don't depend on the table's page/filter state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [counts, filters, analyticsRes] = await Promise.all([
          fetchCocoStoreDashboardCounts(),
          fetchCocoStoreFilterData(),
          fetchCocoStoreAnalytics(),
        ]);
        if (cancelled) return;
        setDashboardCounts(counts || {});
        setFilterOptions({
          creators: toSelectOptions(pickSeries(filters, ["creators", "Creators", "creator_list", "creatorList"])),
          cities: toSelectOptions(pickSeries(filters, ["cities", "Cities", "city_list", "cityList"])),
          states: toSelectOptions(pickSeries(filters, ["states", "States", "state_list", "stateList"])),
        });
        // If the endpoint returns one combined monthly series instead of three
        // separate keys, fall back to reading store/rent/sd figures off each
        // point of that same array.
        const flatSeries = Array.isArray(analyticsRes) ? analyticsRes : [];
        const resolveSeries = (keyCandidates, valueKeys) => {
          const series = pickSeries(analyticsRes, keyCandidates);
          return normalizeSeries(series.length ? series : flatSeries, valueKeys);
        };
        setAnalytics({
          monthlyStoreCreation: resolveSeries(
            ["monthlyStores", "monthly_stores", "monthlyStoreCreation", "monthly_store_creation", "storeCreationTrend", "storesCreatedPerMonth"],
            ["count", "storesCreated", "stores_created", "total", "value"]
          ),
          rentCollectionTrend: resolveSeries(
            ["rentCollectionTrend", "rent_collection_trend", "rentCollection", "rent_collection"],
            ["rentCollected", "rent_collected", "rentAmount", "rent_amount", "amount", "value"]
          ),
          sdCollectionTrend: resolveSeries(
            ["sdCollectionTrend", "sd_collection_trend", "sdCollection", "sd_collection"],
            ["sdCollected", "sd_collected", "sdAmount", "sd_amount", "amount", "value"]
          ),
        });
      } catch (err) {
        if (!cancelled) errorToast(err?.message || "Failed to load overview data");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced so rapid changes (typing in the search box, quick successive
  // filter picks) collapse into a single trailing request instead of one per
  // change. `fetchSeqRef` guards against a slow, superseded response landing
  // after a newer one and overwriting the table with stale data.
  const fetchSeqRef = useRef(0);
  const debouncedFetchStoreListRef = useRef(
    debounce((params, seq, onSuccess, onError) => {
      fetchCocoStoreList(params).then(onSuccess).catch(onError);
    }, 400)
  );

  useEffect(() => {
    const debouncedFetch = debouncedFetchStoreListRef.current;
    return () => debouncedFetch.cancel();
  }, []);

  useEffect(() => {
    const seq = ++fetchSeqRef.current;
    debouncedFetchStoreListRef.current(
      {
        page: currentPage,
        limit: pageSize,
        search: filterData?.inputValue,
        search_column: filterData?.selectColumn,
        creator_id: filterData?.creator_id,
        city: filterData?.city,
        state: filterData?.state,
        rent_status: filterData?.rent_status,
        sd_status: filterData?.sd_status,
        created_from: filterData?.from,
        created_to: filterData?.to,
        sortBy: filterData?.sort_by,
        sortOrder: filterData?.sort_order,
      },
      seq,
      (res) => {
        if (fetchSeqRef.current !== seq) return;
        setError(false);
        setStoreList(res?.data || []);
        setDataCount(res?.total ?? 0);
        setLoading(false);
      },
      (err) => {
        if (fetchSeqRef.current !== seq) return;
        setError(true);
        errorToast(err?.message || "Failed to load store list");
        setLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filterData]);

  const handleFilterClick = useCallback(() => {
    setShowFilter((prev) => !prev);
  }, []);

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currentPage,
  };

  const tableBodyData = storeList?.map((item) => ({
    id: item?.store_id,
    store_code: item?.store_code ?? "--",
    store_name: item?.store_name ?? "--",
    city: item?.city ?? "--",
    state: item?.state ?? "--",
    broker_name: item?.broker_name ?? "--",
    creator_name: item?.creator_name ?? "--",
    approver_name: item?.approver_name ?? "--",
    store_status: item?.store_status ? <BadgesComponent status={item.store_status} /> : "--",
    rent_status: item?.rent_status ? <BadgesComponent status={item.rent_status} /> : "--",
    sd_status: item?.sd_status ? <BadgesComponent status={item.sd_status} /> : "--",
    rent_due: item?.rent_due != null ? `₹ ${Number(item.rent_due).toLocaleString()}` : "--",
    sd_due: item?.sd_due != null ? `₹ ${Number(item.sd_due).toLocaleString()}` : "--",
    created_date: item?.created_date ? moment(item.created_date).format("DD MMM YYYY") : "--",
  }));

  const statSummary = (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 px-[24px] pt-[16px]">
      {STAT_TILES.map((tile) => {
        const tone = TONE_CLASSES[tile.tone];
        const Icon = tile.icon;
        const rawValue = dashboardCounts?.[tile.key] ?? 0;
        const value = tile.isCurrency
          ? `₹ ${Number(rawValue).toLocaleString()}`
          : Number(rawValue).toLocaleString();
        return (
          <TransSummaryComp
            key={tile.key}
            sum={{
              conClass: `${tone.bgClass} rounded-[12px] h-[5.3rem] p-3 flex justify-between`,
              label: tile.label,
              value,
              icon: <Icon size={20} color={tone.fg} />,
              labelClass:
                "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
              valueClass:
                "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
            }}
          />
        );
      })}
    </div>
  );

  const chartsRow = (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 px-[24px] pt-[16px]">
      <ChartCard title="Store Status">
        <StoreStatusPieChart counts={dashboardCounts} />
      </ChartCard>
      <ChartCard title="Monthly Store Creation">
        <MonthlyCreationBarChart data={analytics.monthlyStoreCreation} />
      </ChartCard>
      <ChartCard title="Rent Collection Trend">
        <CollectionTrendChart data={analytics.rentCollectionTrend} color="var(--dashboard-success)" />
      </ChartCard>
      <ChartCard title="SD Collection Trend">
        <CollectionTrendChart data={analytics.sdCollectionTrend} color="var(--dashboard-purple)" />
      </ChartCard>
    </div>
  );

  const filterby = showFilter ? (
    <CocoOverviewFilter
      filterData={filterData}
      setFilterData={setFilterData}
      filterOptions={filterOptions}
      setCurrentPage={setCurrentPage}
    />
  ) : null;

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module={MODULE_NAME}
      list={true}
      title="COCO Store Overview"
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <></>
      ) : (
        <DynamicTransactionTable
          is_checkbox_req={false}
          menu={false}
          list={false}
          date={true}
          filter={true}
          filterSec={true}
          permissionFn={permissionFn}
          module={MODULE_NAME}
          label="Store"
          showFilter={showFilter}
          filterFn={handleFilterClick}
          setFilterData={setFilterData}
          filterData={filterData}
          paginationProps={paginationProps}
          tableHeading={tabHeading}
          tableBodyData={tableBodyData}
          placeholder="Search (/)"
          filterBy={filterby}
          initialFilter={initialFilter}
          summaryArray={statSummary}
          paymentArray={chartsRow}
          orders={storeList}
          setOrders={setStoreList}
          invoiceItems={[]}
          setInvoiceItems={() => {}}
          transactionKey="store"
          id_name="store_id"
          path="store"
          ViewAction={() => {}}
        />
      )}
    </InnerLayout>
  );
};

export default CocoStoreOverview;
